import { client } from '@blazeteams/sdk/client';
import { useAuthStore } from '@/store/auth-store';
import { requireCurrentSession, sessionRevision, sessionScope } from '@/features/auth/session-scope';

type Claims = { user_id: string; tenant_id: string; role?: string; typ?: string };
export type SessionRequest = { revision: number; scope: string; user: string; tenant: string; base: string; bearer: string };
type Renewal = { kind: 'ready'; token: string } | { kind: 'refused' } | { kind: 'unconfirmed' };
type Flight = { owner: SessionRequest; refresh: string; access: string; promise: Promise<Renewal> };
let flight: Flight | undefined;
const object = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);
const bare = (value: string) => value.startsWith('Bearer ') ? value.slice(7) : value;

// Contents are consistency checks only. The API verifies the signature/authority.
function claims(value: unknown): Claims | undefined {
  if (typeof value !== 'string' || !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(value)) return;
  try {
    const payload = value.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const text = new TextDecoder('utf-8', { fatal: true }).decode(Uint8Array.from(atob(payload), c => c.charCodeAt(0)));
    const v: unknown = JSON.parse(text);
    if (object(v) && typeof v.user_id === 'string' && v.user_id && typeof v.tenant_id === 'string' && v.tenant_id &&
        (v.role === undefined || typeof v.role === 'string') && (v.typ === undefined || typeof v.typ === 'string')) return v as Claims;
  } catch { /* Missing/malformed context does not authorize session renewal. */ }
}
function apiBase(raw: unknown, against: string): string | undefined {
  if (typeof raw !== 'string' || !raw) return;
  try {
    const base = new URL(raw, against);
    if (!['https:', 'http:'].includes(base.protocol) || base.username || base.password || base.search || base.hash) return;
    return base.href.replace(/\/+$/, '');
  } catch { /* A per-call capability/base is not a session endpoint. */ }
}
export function sessionAPIBase(request: Request, rawBase: unknown): string | undefined {
  const base = apiBase(client.getConfig().baseUrl, request.url), target = apiBase(rawBase, request.url);
  if (!base || target !== base || !request.url.startsWith(base + '/')) return;
  return base;
}
export function captureSessionRequest(request: Request, base: string): SessionRequest | undefined {
  const state = useAuthStore.getState(), token = state.token && bare(state.token), c = claims(token);
  if (!state.user || !token || !c || c.user_id !== state.user.id || (c.typ && c.typ !== 'access') || state.impersonation ||
      request.headers.get('Authorization') !== 'Bearer ' + token) return;
  return { revision: sessionRevision(), scope: sessionScope(state), user: state.user.id, tenant: c.tenant_id, base, bearer: 'Bearer ' + token };
}
export function requireSessionRequest(owner: SessionRequest, signal?: AbortSignal) {
  requireCurrentSession(owner.revision); signal?.throwIfAborted();
  if (sessionScope(useAuthStore.getState()) !== owner.scope || apiBase(client.getConfig().baseUrl, owner.base) !== owner.base)
    throw new DOMException('Aborted', 'AbortError');
}
function currentToken(owner: SessionRequest): string | undefined {
  const state = useAuthStore.getState(), token = state.token && bare(state.token), c = claims(token);
  if (token && c?.user_id === owner.user && c.tenant_id === owner.tenant && (!c.typ || c.typ === 'access')) return token;
}
function checkedPair(raw: unknown, owner: SessionRequest) {
  if (!object(raw) || raw.success === false || raw.needs_tenant_choice === true || raw.intent_token ||
      (raw.memberships !== undefined && (!Array.isArray(raw.memberships) || raw.memberships.length > 0)) || !object(raw.user) ||
      typeof raw.token !== 'string' || typeof raw.refresh_token !== 'string' || raw.token === raw.refresh_token ||
      raw.user.id !== owner.user || typeof raw.user.name !== 'string' || typeof raw.user.email !== 'string' ||
      typeof raw.user.role !== 'string' || !raw.user.role) return;
  const access = claims(raw.token), refresh = claims(raw.refresh_token);
  if (access?.typ !== 'access' || refresh?.typ !== 'refresh' || access.user_id !== owner.user || refresh.user_id !== owner.user ||
      access.tenant_id !== owner.tenant || refresh.tenant_id !== owner.tenant || access.role !== raw.user.role || refresh.role !== raw.user.role) return;
  return { token: raw.token, refresh: raw.refresh_token, role: raw.user.role };
}
async function perform(owner: SessionRequest, language: string, access: string, refresh: string): Promise<Renewal> {
  const controller = new AbortController();
  const stop = useAuthStore.subscribe(() => {
    if (sessionRevision() !== owner.revision || sessionScope(useAuthStore.getState()) !== owner.scope) controller.abort();
  });
  const timeout = setTimeout(() => controller.abort(), 15_000);
  try {
    const response = await fetch(owner.base + '/auth/refresh', {
      method: 'POST', signal: controller.signal, cache: 'no-store',
      headers: { 'Content-Type': 'application/json', 'Accept-Language': language },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    requireSessionRequest(owner); controller.signal.throwIfAborted();
    let state = useAuthStore.getState();
    if (state.token !== access || state.refreshToken !== refresh) {
      const token = currentToken(owner);
      return token ? { kind: 'ready', token } : { kind: 'unconfirmed' };
    }
    if (![200, 401, 403].includes(response.status)) return { kind: 'unconfirmed' };
    const raw: unknown = await response.json();
    requireSessionRequest(owner); controller.signal.throwIfAborted();
    state = useAuthStore.getState();
    if (state.token !== access || state.refreshToken !== refresh) {
      const token = currentToken(owner);
      return token ? { kind: 'ready', token } : { kind: 'unconfirmed' };
    }
    if (response.status !== 200) {
      // AuthHandler uses core ErrorResponse. An HTML/proxy or incoherent
      // failure is not a confirmed native reason to discard current identity.
      return object(raw) && raw.error === 'error' && raw.code === response.status &&
        typeof raw.message === 'string' && !!raw.message ? { kind: 'refused' } : { kind: 'unconfirmed' };
    }
    const pair = checkedPair(raw, owner);
    if (!pair || !state.user) return { kind: 'unconfirmed' };
    // Keep newer personal preference/profile edits. Only the confirmed effective
    // role follows this replacement JWT; /auth/me owns other profile hydration.
    useAuthStore.getState().setAuth(pair.token, pair.refresh, { ...state.user, role: pair.role });
    return { kind: 'ready', token: pair.token };
  } catch {
    requireSessionRequest(owner);
    const state = useAuthStore.getState();
    if (state.token !== access || state.refreshToken !== refresh) {
      const token = currentToken(owner);
      if (token) return { kind: 'ready', token };
    }
    return { kind: 'unconfirmed' };
  } finally { clearTimeout(timeout); stop(); }
}
export async function renewSession(owner: SessionRequest, language: string): Promise<Renewal> {
  requireSessionRequest(owner);
  const state = useAuthStore.getState(), current = currentToken(owner);
  if (current && 'Bearer ' + current !== owner.bearer) return { kind: 'ready', token: current };
  if (!state.token || !state.refreshToken) return { kind: 'unconfirmed' };
  const refreshOwner = claims(state.refreshToken);
  if (!refreshOwner || refreshOwner.user_id !== owner.user || refreshOwner.tenant_id !== owner.tenant ||
      (refreshOwner.typ && refreshOwner.typ !== 'refresh')) return { kind: 'unconfirmed' };
  if (!flight || flight.owner.revision !== owner.revision || flight.owner.base !== owner.base ||
      flight.refresh !== state.refreshToken || flight.access !== state.token) {
    const pending: Flight = { owner, access: state.token, refresh: state.refreshToken, promise: perform(owner, language, state.token, state.refreshToken) };
    flight = pending;
    void pending.promise.then(() => { if (flight === pending) flight = undefined; }, () => { if (flight === pending) flight = undefined; });
  }
  return flight.promise;
}
